import {
  DEFAULT_RECURRENCE_DRAFT,
  recurrenceAllowsDate,
  recurrenceFieldsFromMetadata
} from './recurrence.js'

export { recurrenceAllowsDate } from './recurrence.js'

// Month and weekday names are polyglot KEYS, written out one literal per
// entry (never built from an index — I18N-CONVENTION §1.3). Every render site
// resolves them through `el.call('polyglot', …)`; these helpers stay pure and
// element-free, so they remain unit-testable and reusable off the DOM.
const MONTH_NAMES = [
  'calendar.month.january',
  'calendar.month.february',
  'calendar.month.march',
  'calendar.month.april',
  'calendar.month.may',
  'calendar.month.june',
  'calendar.month.july',
  'calendar.month.august',
  'calendar.month.september',
  'calendar.month.october',
  'calendar.month.november',
  'calendar.month.december'
]

const DAY_NAMES = [
  'calendar.day.sun',
  'calendar.day.mon',
  'calendar.day.tue',
  'calendar.day.wed',
  'calendar.day.thu',
  'calendar.day.fri',
  'calendar.day.sat'
]

// The intentionally small default roster for Calendar's Team surfaces.
// Selectors use directory usernames/email prefixes rather than projected
// emails: non-management viewers receive privacy-safe people rows whose
// `email` can be blank or replaced with the current viewer's address.
export const CALENDAR_DEFAULT_TEAM_MEMBER_SELECTORS = [
  'allen@symbols.app',
  'bala@symbols.app',
  'ha@symbols.app',
  'jerry@symbols.app',
  'lekya@symbols.app',
  'luka@symbols.app',
  'manju@symbols.app',
  'marina layus',
  'nikoloza',
  'scott@symbols.app',
  'tengiz@symbols.app',
  'thomas.gazaniga@symbols.app',
  'thomas@symbols.app',
  'svinchy@symbols.app',
  'toko@symbols.app',
  'zachary@symbols.app',
  'zajim@symbols.app'
]

const _teamText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
const _teamWords = (value) =>
  _teamText(value)
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')

const _teamSelectorAliases = (selector) => {
  const local = _teamText(selector).split('@')[0]
  if (local === 'toko') return ['toko', 'tako']
  if (local === 'zachary') return ['zachary', 'zach']
  return [local]
}

const _teamDirectoryNameAliases = {
  josh: 'joshua mcmillan',
  nikoloza: 'nika tomadze',
  svinchy: 'tornike svintradze',
  toko: 'tako',
  zachary: 'zach betzen'
}

const _teamMemberMatchesSelector = (person, selector, uniqueEmails) => {
  const raw = _teamText(selector)
  const aliases = _teamSelectorAliases(raw).map(_teamWords)
  const email = _teamText(person?.email)
  if (email && uniqueEmails.has(email) && email === raw) return true

  const username = _teamWords(person?.username || person?.slug)
  const name = _teamWords(person?.name)
  return aliases.some(
    (alias) => alias && (username === alias || name === alias || name.startsWith(`${alias} `))
  )
}

export function calendarTeamMemberKey(person) {
  if (!person) return ''
  const id = person.userId ?? person.id
  if (id != null && String(id)) return `member:id:${String(id)}`
  const username = _teamText(person.username || person.slug)
  if (username) return `member:username:${username}`
  const name = _teamText(person.name)
  if (name) return `member:name:${name}`
  const email = _teamText(person.email)
  return email ? `member:email:${email}` : ''
}

export function calendarParticipantRsvpKeys(person) {
  if (!person) return []
  const keys = [calendarTeamMemberKey(person)]
  const email = _teamText(typeof person === 'string' ? person : person.email)
  if (email) keys.push(`member:email:${email}`)
  return [...new Set(keys.filter(Boolean))]
}

export function calendarEventRsvpStatus(event, person) {
  const metadata = event?.integrationMetadata || {}
  const responses = metadata.calendarParticipantResponses || {}
  const occurrenceResponses = metadata.calendarParticipantResponseOverrides?.[event?.date] || {}
  const responseChanges = metadata.calendarParticipantResponseChanges || {}
  for (const key of calendarParticipantRsvpKeys(person)) {
    if (occurrenceResponses[key]) return occurrenceResponses[key]
    const datedResponse = (responseChanges[key] || []).reduce((latest, change) => {
      if (!change?.from || change.from > event?.date) return latest
      return !latest || change.from >= latest.from ? change : latest
    }, null)
    if (datedResponse?.status) return datedResponse.status
    if (responses[key]) return responses[key]
  }
  return ''
}

export function calendarEventDeclinedFor(event, person) {
  return calendarEventRsvpStatus(event, person) === 'declined'
}

// Shared consumer domains cannot establish a teammate's identity by name.
// Exact email and stable member-key matches remain valid for these providers.
const _publicCalendarMailDomains = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'rocketmail.com',
  'aol.com',
  'aim.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'fastmail.com',
  'fastmail.fm',
  'hey.com',
  'mail.com',
  'email.com',
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'web.de',
  't-online.de',
  'yandex.com',
  'yandex.ru',
  'ya.ru',
  'mail.ru',
  'inbox.ru',
  'list.ru',
  'bk.ru',
  'qq.com',
  'foxmail.com',
  '163.com',
  '126.com',
  'naver.com',
  'daum.net',
  'hanmail.net'
])
const _publicCalendarMailDomain = (domain) =>
  _publicCalendarMailDomains.has(domain) ||
  /^(?:yahoo|hotmail|outlook|live|msn|aol|gmx|yandex)\.(?:[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(
    domain
  )

// Exact email identity wins. Privacy-projected names are only a fallback
// within a domain established by the directory or signed-in user.
export function calendarEventForTeamMember(event, person, people = [], viewerEmail = '') {
  if (!event || !person || calendarEventDeclinedFor(event, person)) return false
  const memberKey = calendarTeamMemberKey(person)
  const roster = people.length ? people : [person]
  const domain = (email) => _teamText(email).split('@')[1] || ''
  const domains = new Set(
    [...roster.map((member) => domain(member.email)), domain(viewerEmail)].filter(Boolean)
  )
  const matches = (email) => {
    const normalized = _teamText(email)
    if (!normalized || !domain(normalized)) return false
    const exact = roster.filter((member) => _teamText(member.email) === normalized)
    if (exact.length === 1) return calendarTeamMemberKey(exact[0]) === memberKey
    if (_publicCalendarMailDomain(domain(normalized)) || !domains.has(domain(normalized)))
      return false
    const owner = calendarFindTeamMember(roster, normalized)
    // A known, unique email must never be replaced by a name-based guess.
    const ownerEmail = _teamText(owner?.email)
    if (
      ownerEmail &&
      ownerEmail !== normalized &&
      roster.filter((member) => _teamText(member.email) === ownerEmail).length === 1
    )
      return false
    return Boolean(memberKey && calendarTeamMemberKey(owner) === memberKey)
  }
  if (event.googleCalendarId) return matches(event.googleCalendarId)
  return (
    (event.integrationMetadata?.calendarInviteeMemberKeys || []).includes(memberKey) ||
    matches(event.creatorEmail) ||
    (event.invitees || []).some(matches)
  )
}

// Merge only provider-identical copies of the same occurrence, never titles.
// Prefer the viewer's copy so their RSVP and detail identity remain canonical.
export function calendarUniqueEvents(events, viewerEmail) {
  const unique = new Map()
  for (const event of events || []) {
    const uid = event.googleEventId
    const key = uid ? JSON.stringify([uid, event.startAt || event.date, event.startTime]) : event
    const previous = unique.get(key)
    if (!previous || event.googleCalendarId === viewerEmail) unique.set(key, event)
  }
  return [...unique.values()]
}

export function calendarBookingStartsInPast(booking, now = new Date()) {
  if (!booking?.date) return false
  const [year, month, day] = String(booking.date).split('-').map(Number)
  const [hours, minutes] = String(booking.allDay ? '00:00' : booking.startTime || '')
    .split(':')
    .map(Number)
  if (![year, month, day, hours, minutes].every(Number.isFinite)) return false
  const boundary = new Date(now)
  boundary.setSeconds(0, 0)
  if (booking.allDay) boundary.setHours(0, 0, 0, 0)
  return new Date(year, month - 1, day, hours, minutes).getTime() < boundary.getTime()
}

export function calendarBookingHasInvalidRange(booking) {
  if (booking?.allDay || !booking?.date || !booking?.startTime || !booking?.endTime) return false
  const endDate = booking.endDate || booking.date
  if (endDate < booking.date) return true
  return endDate === booking.date && booking.endTime <= booking.startTime
}

const _rsvpSnapshotKey = (eventId) => `calendar:rsvp:${String(eventId || '')}`
const _rsvpEventFingerprint = (event) =>
  [event?.title, event?.date, event?.startTime, event?.endTime]
    .map((value) => value || '')
    .join('|')
const _rsvpStorage = () => {
  if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
  if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
    return globalThis.localStorage
  }
  return null
}

export function cacheCalendarEventRsvp(event, pendingStatus = '', pendingScope = '') {
  if (!event?.id) return
  const snapshot = {
    event,
    pendingStatus: pendingStatus || '',
    pendingScope: pendingScope || ''
  }
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    try {
      const url = new URL(window.location.href)
      if (pendingStatus) {
        const eventSnapshot = {
          id: event.id,
          title: event.title,
          date: event.date,
          startTime: event.startTime,
          endTime: event.endTime,
          startAt: event.startAt,
          endAt: event.endAt,
          googleEventId: event.googleEventId
        }
        url.searchParams.set('eventSeries', _baseEventId(event.googleEventId) || '')
        url.searchParams.set('eventFingerprint', _rsvpEventFingerprint(event))
        url.searchParams.set('eventSnapshot', JSON.stringify(eventSnapshot))
        url.searchParams.set('eventRsvp', pendingStatus)
        url.searchParams.set('eventRsvpScope', pendingScope || 'future')
      }
      window.history.replaceState(null, '', url.toString())
    } catch (_) {}
  }
  const storage = _rsvpStorage()
  if (!storage) return
  try {
    storage.setItem(_rsvpSnapshotKey(event.id), JSON.stringify(snapshot))
  } catch (_) {}
}

export function calendarEventRsvpSnapshot(eventId) {
  if (!eventId) return null
  const storage = _rsvpStorage()
  if (!storage) return null
  try {
    const snapshot = JSON.parse(storage.getItem(_rsvpSnapshotKey(eventId)) || 'null')
    return snapshot?.event ? snapshot : null
  } catch (_) {
    return null
  }
}

export function calendarVisibleTeamMembers(people, selected = []) {
  const all = Array.isArray(people) ? people : []
  const selectors = selected.length ? selected : CALENDAR_DEFAULT_TEAM_MEMBER_SELECTORS

  // Once the user removes a column, the selection is materialised as stable
  // member keys so two people with the same first name remain independent.
  if (selectors.every((selector) => _teamText(selector).startsWith('member:'))) {
    const byKey = new Map(all.map((person) => [calendarTeamMemberKey(person), person]))
    return selectors.map((selector) => byKey.get(selector)).filter(Boolean)
  }

  const emailCounts = new Map()
  for (const person of all) {
    const email = _teamText(person?.email)
    if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1)
  }
  const uniqueEmails = new Set(
    Array.from(emailCounts.entries())
      .filter(([, count]) => count === 1)
      .map(([email]) => email)
  )

  return all.filter((person) =>
    selectors.some((selector) => _teamMemberMatchesSelector(person, selector, uniqueEmails))
  )
}

export function calendarTeamMembersWithout(people, selected, person) {
  const removeKey = calendarTeamMemberKey(person)
  if (!removeKey) return selected || []
  return calendarVisibleTeamMembers(people, selected)
    .map(calendarTeamMemberKey)
    .filter((key) => key && key !== removeKey)
}

export function calendarFindTeamMember(people, query) {
  const all = Array.isArray(people) ? people : []
  const raw = _teamText(query)
  if (!raw) return null

  const local = _teamWords(raw.split('@')[0])
  const words = _teamWords(raw)
  const exactEmailMatches = all.filter((person) => _teamText(person?.email) === raw)
  if (exactEmailMatches.length === 1) return exactEmailMatches[0]

  const aliasedName = _teamDirectoryNameAliases[local]
  if (aliasedName) {
    const aliasedPerson = all.find((person) => _teamWords(person?.name) === aliasedName)
    if (aliasedPerson) return aliasedPerson
  }

  const exactUsername = all.find((person) => _teamWords(person?.username || person?.slug) === local)
  if (exactUsername) return exactUsername

  const exactName = all.find((person) => {
    const name = _teamWords(person?.name)
    return name === words || name === local
  })
  if (exactName) return exactName

  const firstNameMatches = all.filter((person) => _teamWords(person?.name).split(' ')[0] === local)
  return firstNameMatches.length === 1 ? firstNameMatches[0] : null
}

export function calendarTeamMemberSuggestions(people, query, selected = [], limit = 6) {
  const all = Array.isArray(people) ? people : []
  const raw = _teamText(query)
  if (!raw) return []

  const local = _teamWords(raw.split('@')[0])
  const words = _teamWords(raw)
  const aliasedName = _teamDirectoryNameAliases[local]
  const visibleKeys = new Set(calendarVisibleTeamMembers(all, selected).map(calendarTeamMemberKey))
  const emailCounts = new Map()
  for (const person of all) {
    const email = _teamText(person?.email)
    if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1)
  }

  return all
    .map((person, index) => {
      if (visibleKeys.has(calendarTeamMemberKey(person))) return null

      const email = _teamText(person?.email)
      const emailLocal = _teamWords(email.split('@')[0])
      const usernameValue = _teamText(person?.username || person?.slug)
      const username = _teamWords(usernameValue)
      const name = _teamWords(person?.name)
      const nameParts = name.split(' ')
      const uniqueEmail = email && emailCounts.get(email) === 1
      let score = Number.POSITIVE_INFINITY

      if (uniqueEmail && email === raw) score = 0
      else if (username && username === local) score = 1
      else if (aliasedName && name === aliasedName) score = 2
      else if (name === words || name === local || (uniqueEmail && emailLocal === local)) score = 3
      else if (username && username.startsWith(local)) score = 4
      else if (name.startsWith(words) || name.startsWith(local)) score = 5
      else if (nameParts.some((part) => part.startsWith(local))) score = 6
      else if (uniqueEmail && (email.includes(raw) || emailLocal.includes(local))) score = 7

      if (!Number.isFinite(score)) return null
      const identity = usernameValue ? `@${usernameValue}` : uniqueEmail ? email : ''
      return { person: { ...person, _calendarTeamIdentity: identity }, score, index }
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .slice(0, Math.max(0, limit))
    .map(({ person }) => person)
}

export function calendarTeamMembersWith(people, selected, person) {
  const addKey = calendarTeamMemberKey(person)
  const current = calendarVisibleTeamMembers(people, selected)
    .map(calendarTeamMemberKey)
    .filter(Boolean)
  if (!addKey || current.includes(addKey)) return current
  return [...current, addKey]
}

// CalHeader.Title / CalMain's own nav read `getMonthLabel(root.currentYear,
// root.currentMonth)` straight off ROOT state — real usage always has a
// CalMain-family ancestor seed both before this ever renders (TodayBtn's
// onClick, page boot, …). A bare mount (catalog `if:false`-scoped MCP
// probes; SSR/hydration races before that seed lands) has neither, so
// `MONTH_NAMES[undefined]` -> `undefined`, stringifying to the literal
// "undefined undefined" (tickets/sonnet.md CAT-3). "Today" is the only
// honest default for an unset calendar cursor — same defensive-fallback
// class as `getWeekDays` below.
function _yearOrToday(year) {
  return Number.isFinite(year) ? year : new Date().getFullYear()
}
function _monthOrToday(month) {
  return Number.isFinite(month) ? month : new Date().getMonth()
}

export function getMonthName(year, month) {
  return MONTH_NAMES[_monthOrToday(month)]
}

// Returns `<month-key> <year>` — the KEY and the year, space-separated. A
// render site resolves the key half: `getMonthName` gives the key alone, so a
// caller that needs the composed label translates the name and appends the
// year itself (see CalMain.MonthLabel / CalMiniCalendar.MonthLabel).
export function getMonthLabel(year, month) {
  return `${MONTH_NAMES[_monthOrToday(month)]} ${_yearOrToday(year)}`
}

// The year that pairs with `getMonthName(year, month)` — kept beside it so a
// render site never has to re-implement the "unset cursor → today" fallback.
export function getMonthYear(year) {
  return _yearOrToday(year)
}

export function getDayNames() {
  return DAY_NAMES
}

export function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrevMonth = new Date(year, month, 0).getDate()
  const days = []

  // Normalize previous month (handles January -> December of previous year)
  const prevMonthDate = new Date(year, month - 1, 1)
  const prevYear = prevMonthDate.getFullYear()
  const prevMonth = prevMonthDate.getMonth()

  // Pad start with previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ day: daysInPrevMonth - i, month: prevMonth, year: prevYear, isOtherMonth: true })
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, month, year, isOtherMonth: false })
  }

  // Normalize next month (handles December -> January of next year)
  const nextMonthDate = new Date(year, month + 1, 1)
  const nextYear = nextMonthDate.getFullYear()
  const nextMonth = nextMonthDate.getMonth()

  // Pad end to complete 6 rows (42 cells)
  const remaining = 42 - days.length
  for (let d = 1; d <= remaining; d++) {
    days.push({ day: d, month: nextMonth, year: nextYear, isOtherMonth: true })
  }

  return days
}

export function isToday(year, month, day) {
  const t = new Date()
  return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day
}

export function formatDate(year, month, day) {
  const m = String(month + 1).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${year}-${m}-${d}`
}

const _DAY_MS = 86400000

// Greedy lane packing — assigns each event a `_lane` (0-indexed column)
// and `_lanes` (total columns its overlap cluster needs). Used by the week
// view to render side-by-side overlapping events without them stacking on
// top of each other. O(n²) but n is small (events on one day).
const _toMins = (t) => {
  const [h, m] = (t || '00:00').split(':').map(Number)
  return h * 60 + m
}
export function assignLanes(evs) {
  if (!evs.length) return evs
  // Shallow-clone every event so we don't mutate the canonical events array.
  const sorted = evs
    .map((e) => ({ ...e }))
    .sort((a, b) => _toMins(a.startTime) - _toMins(b.startTime))
  // Group into overlap clusters: events that transitively overlap end up in
  // the same cluster and share the same lane count.
  const clusters = []
  let cur = null
  let curEnd = -1
  for (const ev of sorted) {
    const s = _toMins(ev.startTime)
    const e = _toMins(ev.endTime)
    if (cur && s < curEnd) {
      cur.push(ev)
      if (e > curEnd) curEnd = e
    } else {
      cur = [ev]
      clusters.push(cur)
      curEnd = e
    }
  }
  for (const cluster of clusters) {
    // Assign lanes within the cluster — pack each event into the lowest lane
    // whose last event has already ended.
    const laneEnds = []
    for (const ev of cluster) {
      const s = _toMins(ev.startTime)
      const e = _toMins(ev.endTime)
      let lane = laneEnds.findIndex((end) => end <= s)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(e)
      } else {
        laneEnds[lane] = e
      }
      ev._lane = lane
    }
    const total = laneEnds.length
    for (const ev of cluster) ev._lanes = total
  }
  return sorted
}

// Cross-midnight tail: an event with `endTime <= startTime` actually runs
// past midnight into the next day. The start day shows the [startTime, 24:00]
// portion (clipped via eventToHeight); this helper produces a synthetic
// [00:00, endTime] piece for the next day.
const _isCrossMidnight = (ev) => {
  if (!ev?.startTime || !ev?.endTime) return false
  const [sh, sm] = ev.startTime.split(':').map(Number)
  const [eh, em] = ev.endTime.split(':').map(Number)
  return eh * 60 + em <= sh * 60 + sm
}

export function getEventsForDay(events, year, month, day) {
  const dateStr = formatDate(year, month, day)
  const target = new Date(year, month, day)
  const prevDate = formatDate(year, month, day - 1) // JS Date normalizes day=0/-1

  const out = []
  for (const ev of events || []) {
    // Cross-midnight tail from yesterday → render as a [00:00, endTime] piece.
    if (ev.date === prevDate && _isCrossMidnight(ev)) {
      // The tail belongs to YESTERDAY's occurrence — if that occurrence is
      // excluded (or otherwise disallowed by the rule), its tail must not
      // render either.
      if (
        ev.recurring &&
        ev.recurring !== 'none' &&
        !recurrenceAllowsDate(ev, new Date(year, month, day - 1))
      ) {
        continue
      }
      out.push({ ...ev, startTime: '00:00', _isCrossTail: true })
      continue
    }
    if (ev.date === dateStr) {
      if (ev.recurring && ev.recurring !== 'none' && !recurrenceAllowsDate(ev, target)) {
        continue
      }
      out.push(ev)
      continue
    }
    if (!ev.recurring || ev.recurring === 'none') continue
    if (recurrenceAllowsDate(ev, target)) out.push({ ...ev, date: dateStr })
  }
  return out
}

// ─── Time helpers (shared by all views) ──────────────────────────────────

export const HOUR_PX = 56
export const SLOT_MIN = 15

// Intl.DateTimeFormat is expensive to construct (one of the slowest things
// in JS). Cache one per timezone so the hot loops in availability/header
// renders don't allocate hundreds per minute tick.
const _timeFmt = new Map()
const _dateFmt = new Map()

// WHICH CLOCK. #2265 made every calendar view show ONE clock, and chose the
// 12-hour one. The point was one clock across the views, not AM / PM: in a
// Georgian session `4:00 PM` is Latin text on a Georgian calendar, and a
// 12-hour clock is not how the language tells time. So the clock follows the
// SESSION LANGUAGE — every view still agrees, because every view asks here.
// A caller with no language gets English, which is what it got before.
const CALENDAR_HOUR12_LANGS = ['en']
export const calendarHour12 = (lang) =>
  CALENDAR_HOUR12_LANGS.includes(
    String(lang || 'en')
      .toLowerCase()
      .split('-')[0]
  )

// The DISPLAY clock of a zone. Digits are Latin in both languages, so the
// Georgian branch needs no Georgian `Intl` data (Chrome ships none). `tz`
// undefined = the browser's own zone.
export const formatTzTime = (ms, tz, lang) => {
  const hour12 = calendarHour12(lang)
  const cacheKey = `${tz}|${hour12 ? 12 : 24}`
  let f = _timeFmt.get(cacheKey)
  if (!f) {
    f = hour12
      ? new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      : new Intl.DateTimeFormat('en-GB', {
          timeZone: tz,
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23'
        })
    _timeFmt.set(cacheKey, f)
  }
  return f.format(ms)
}

// The DATA clock of a zone: minutes since local midnight. Read from the h23
// PARTS, never from display text. The availability math used to parse
// `formatTzTime`; when #2265 turned that into `4:00 PM`, `hhmmToMin` answered
// NaN, every comparison went false, and a member with working hours read as
// unavailable for the whole day (availabilityClock.test.js).
const _clockFmt = new Map()
export const tzClockMinutes = (ms, tz) => {
  let f = _clockFmt.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    })
    _clockFmt.set(tz, f)
  }
  let hour = 0
  let minute = 0
  for (const part of f.formatToParts(ms)) {
    if (part.type === 'hour') hour = Number(part.value) % 24
    else if (part.type === 'minute') minute = Number(part.value)
  }
  return hour * 60 + minute
}

export const formatTzDate = (ms, tz) => {
  let f = _dateFmt.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    _dateFmt.set(tz, f)
  }
  // en-CA returns YYYY-MM-DD already
  return f.format(ms).replace(/\u2010|\u2013/g, '-')
}

export const hhmmToMin = (s) => {
  if (!s) return 0
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

export const minToHHMM = (n) => {
  const total = Math.max(0, Math.min(24 * 60, Math.round(n)))
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const eventUtcMs = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return 0
  const [y, m, d] = dateStr.split('-').map(Number)
  const [h, mm] = timeStr.split(':').map(Number)
  return new Date(y, m - 1, d, h, mm).getTime()
}

// Backwards-compat aliases (private, so existing internal usages don't need a sweep)
const _formatTzDate = formatTzDate
const _hhmmToMin = hhmmToMin

// Returns null (available), or one of 'static' / 'event' for unavailability.
//   'static' = outside working hours (general off-hours, configured in profile)
//   'event'  = busy/OOO event from the events array (custom, ad-hoc)
//
// Events with type='busy' (intra-day, has startTime/endTime) and type='ooo'
// (full-day, may have endDate) on the events list are read inline so the
// helper has a single source of truth shared by every view.
function _availabilityType(ms, member, events) {
  if (!member?.timezone) return null
  const tz = member.timezone
  const localDate = _formatTzDate(ms, tz)
  const cur = tzClockMinutes(ms, tz)

  // Event-type: busy events from the events array. Busy + OOO are merged
  // into a single 'busy' type. If endDate is set (or differs from date),
  // it's a multi-day full-day block. Otherwise it's an intra-day time range.
  for (const ev of events || []) {
    if (ev.type !== 'busy') continue
    if (!(ev.invitees || []).includes(member.email)) continue
    const isMultiDay = ev.endDate && ev.endDate !== ev.date
    if (isMultiDay) {
      if (localDate >= ev.date && localDate <= ev.endDate) return 'event'
    } else {
      if (ev.date !== localDate) continue
      const s = _hhmmToMin(ev.startTime || '00:00')
      const e = _hhmmToMin(ev.endTime || '24:00')
      if (cur >= s && cur < e) return 'event'
    }
  }

  // Static: outside working hours (handles midnight-spanning ranges where end < start)
  const wh = member.workingHours
  if (wh) {
    const start = _hhmmToMin(wh.start)
    const end = _hhmmToMin(wh.end)
    const inHours = start < end ? cur >= start && cur < end : cur >= start || cur < end // wraps midnight
    if (!inHours) return 'static'
  }

  return null
}

/**
 * Walks `totalMinutes` from `baseMs` in `slotMinutes` steps, grouping
 * consecutive same-type unavailable slots into ranges.
 *
 * @param {Object} member         person record with timezone/workingHours
 * @param {number} baseMs         UTC ms timestamp at minute 0 of the window
 * @param {number} totalMinutes   window length (default 24*60)
 * @param {number} slotMinutes    step size (default 15)
 * @param {Array}  events         events list (busy/ooo entries are read here)
 * @returns {Array<{startMin, endMin, type}>}
 */
export function getUnavailableRanges(
  member,
  baseMs,
  totalMinutes = 24 * 60,
  slotMinutes = 15,
  events = []
) {
  if (!member) return []
  // Day columns can render before their `baseMs` reactive prop resolves
  // (state still rehydrating). Skip those frames — Intl.DateTimeFormat
  // .format(NaN) throws "Invalid time value" and would tear down every
  // DayColumn in the smbls 3.14 week view.
  if (!Number.isFinite(baseMs)) return []
  const STEP_MS = slotMinutes * 60 * 1000
  const ranges = []
  let curStart = null
  let curType = null
  const steps = Math.ceil(totalMinutes / slotMinutes)
  for (let i = 0; i <= steps; i++) {
    const isLast = i >= steps
    let type = null
    if (!isLast) {
      type = _availabilityType(baseMs + i * STEP_MS, member, events)
    }
    if (type !== curType || isLast) {
      if (curType != null && curStart != null) {
        ranges.push({ startMin: curStart, endMin: i * slotMinutes, type: curType })
      }
      curStart = type ? i * slotMinutes : null
      curType = type
    }
  }
  return ranges
}

// Returns true if the calendar week containing (year, month, day) also
// contains the user's local "today". Used to gate the NowLine indicator
// in the week view.
export function weekContainsToday(year, month, day) {
  const t = new Date()
  const ty = t.getFullYear()
  const tm = t.getMonth()
  const td = t.getDate()
  const ref = new Date(year, month, day)
  const dow = ref.getDay()
  const start = new Date(year, month, day - dow)
  const end = new Date(year, month, day - dow + 6)
  const today = new Date(ty, tm, td)
  return today >= start && today <= end
}

// Same never-seeded-on-a-bare-mount defect as getMonthLabel above: CalMain's
// week grid (CalWeekView/CalWeekDayHeader) reads
// `getWeekDays(root.currentYear, root.currentMonth, root.currentDay)`
// straight off root state. Unset -> `new Date(undefined, undefined,
// undefined)` is an Invalid Date, `.getDay()` is NaN, and every day cell's
// `.getDate()` below inherits that NaN — live-reproduced as "Sun NaN Mon
// NaN Tue NaN …" across the whole week header (tickets/sonnet.md CAT-3).
export function getWeekDays(year, month, day) {
  const y = _yearOrToday(year)
  const m = _monthOrToday(month)
  const dd = Number.isFinite(day) ? day : new Date().getDate()
  const d = new Date(y, m, dd)
  const dow = d.getDay()
  const days = []
  for (let i = 0; i < 7; i++) {
    const wd = new Date(y, m, dd - dow + i)
    days.push({
      year: wd.getFullYear(),
      month: wd.getMonth(),
      day: wd.getDate(),
      dayName: DAY_NAMES[i]
    })
  }
  return days
}

// The same timeline renders either a full week or the selected date.
export function getTimelineDays(year, month, day, viewMode) {
  const days = getWeekDays(year, month, day)
  if (viewMode !== 'day') return days
  const selected = new Date(
    _yearOrToday(year),
    _monthOrToday(month),
    Number.isFinite(day) ? day : new Date().getDate()
  )
  return days.filter(
    (date) =>
      date.year === selected.getFullYear() &&
      date.month === selected.getMonth() &&
      date.day === selected.getDate()
  )
}

// The hour gutter of every time grid. Called as `el.call('getHours')`, so `this`
// is the element and the label follows the session language (see
// `calendarHour12`); a bare call keeps the English clock.
export function getHours() {
  const lang = this && typeof this.call === 'function' ? this.call('getActiveLang') : 'en'
  const hour12 = calendarHour12(lang)
  const hours = []
  for (let h = 0; h < 24; h++) {
    const label = !hour12
      ? `${String(h).padStart(2, '0')}:00`
      : h === 0
        ? '12 AM'
        : h < 12
          ? `${h} AM`
          : h === 12
            ? '12 PM'
            : `${h - 12} PM`
    hours.push({ h, label })
  }
  return hours
}

// Role helpers live in the workspace-shell library — the canonical
// implementation stays in one place. They are NOT re-exported through this
// module any more: a bag re-export of a value imported from an externalized
// specifier always serializes to "" (frank FA206 stub-phantom), so all seven
// published as empty values. The declared workspace-shell library merges its
// real functions bag into el.context, and calendar components that need one
// at module scope import it straight from roleHelpers.js.
import {
  hasRole as _hasRole,
  isExternalEmail as _isExternalEmail,
  // `export { currentUser } from ...` above is a RE-export — it creates no
  // local binding, so reading bare `currentUser` inside this module is a
  // ReferenceError at runtime (eventHue threw it in every event chip's
  // background/boxShadow/borderColor factory).
  currentUser as _currentUser,
  shellIsSuperAdmin as _shellIsSuperAdmin
} from '@symbo-ls/workspace-shell/functions/roleHelpers.js'

// Should this event be marked private (i.e. hidden as "Unavailable"
// from non-creators) at SAVE time? Calendar-specific policy — kept
// here because it references event-shape fields. True when:
//   - Creator is a superadmin AND it isn't a Company event, OR
//   - Creator is external to the company team
// A role lives in the roster (`root.roles.byUser[userId]`), and a people row
// carries no `roles` array, so the shell's hasRole needs the root: `root`,
// else `this.state.root` (el.call binds `this`). Without it the roster never
// counted, and no superadmin's meeting was stamped private.
export function shouldStampPrivate(creator, type, people, root) {
  if (!creator) return false
  const isExt = _isExternalEmail(creator.email || creator, people)
  if (isExt) return true
  if (type === 'general') return false
  const r = root || (this && this.state && this.state.root) || null
  return _hasRole(creator, 'superadmin', r)
}

// Should this event be hidden as "Unavailable" from the current viewer?
// Private events:
//   - The viewer is not the creator AND
//   - The viewer is not a superadmin (the roster: `root`, else
//     `this.state.root`, as in shouldStampPrivate) AND
//   - The event is flagged isPrivate
// Returns true → render as a generic Unavailable block, no details exposed.
export function eventIsPrivateFor(event, viewer, root) {
  if (!event?.isPrivate) return false
  // Superadmin (the only role allowed to do this for now) can publish a
  // private event to the team via `isTeamShared`. Once it's shared, the
  // event renders normally for everyone.
  if (event.isTeamShared) return false
  if (!viewer) return true
  if (viewer.email === event.creatorEmail) return false
  const r = root || (this && this.state && this.state.root) || null
  if (_hasRole(viewer, 'superadmin', r)) return false
  return true
}

// May the viewer edit or delete this event? The client mirror of the
// server's calendar write rule (calendar mongoStore `_canWriteEvent`, which
// the workspace-project worker applies to every write this client sends):
// the event's organizer — `creatorEmail`, mapped from `organizer_email` —
// compared without case, or a platform superadmin (`symbols_role: 'admin'`,
// or the roster `superadmin` role the worker reads from the token). An
// invitee gets no Edit and no Delete. The server stays the guard; this only
// hides what the server refuses.
//
// An org owner / admin is NOT in the rule: the main server lets them write
// (`isOrgAdmin`, CalendarService), but the worker path this client writes
// through never sets that flag, so the server refuses their write today.
export function calendarCanWriteEvent(event, root) {
  const r = root || (this && this.state && this.state.root) || null
  if (!event || !r) return false
  const me = String(r.currentUserEmail || '')
    .trim()
    .toLowerCase()
  const organizer = String(event.creatorEmail || '')
    .trim()
    .toLowerCase()
  if (me && organizer && me === organizer) return true
  if ((r.jwtClaims || {}).symbols_roster_role === 'superadmin') return true
  return _shellIsSuperAdmin({ root: r }) === true
}

// The event-type hue, resolved once so every calendar surface's tint, inset
// type bar, border and (WeekView) proposal ring can never disagree. Private
// events (superadmin meetings, external creators) mask as a plain green
// "Meeting" chip — no detail leak. Was declared separately (byte-identical
// modulo optional-chaining) in both TeamNowView.js and WeekView.js —
// consolidated here so there's one source of truth, and because the
// duplicate module-scope declarations collided under esbuild's bundle
// (collision-dedup rename shipped an unresolvable `_eventHue2` reference —
// tickets/fable.md "workspace-calendar frank serialization blockers").
export const eventHue = (el, s) => {
  if (eventIsPrivateFor(s, _currentUser(s?.root), s?.root)) return 'green'
  if (s.type === 'general') return 'blue'
  if (s.type === 'busy') return 'red'
  return 'green'
}

// Soonest not-yet-ended meeting/general event the viewer can see — powers
// the sidebar's meeting-alert card (geex frame 10, tickets/
// brief-product-ui-direction.md "Meeting alert card"). Same type gate
// CalMonthDayCell.Events already applies (busy/ooo blocks aren't
// "meetings" to alert about) plus the same eventIsPrivateFor viewer gate
// every other calendar surface uses. Returns null when nothing qualifies.
// `root` is the roster for that gate (else `this.state.root`).
export function nextUpcomingEvent(events, viewer, root) {
  const r = root || (this && this.state && this.state.root) || null
  const now = Date.now()
  let best = null
  let bestStartMs = Infinity
  for (const ev of events || []) {
    if (!ev || !ev.date) continue
    if (ev.type && ev.type !== 'meeting' && ev.type !== 'general') continue
    if (eventIsPrivateFor(ev, viewer, r)) continue
    const endMs = eventUtcMs(ev.endDate || ev.date, ev.endTime || ev.startTime || '23:59')
    if (endMs && endMs < now) continue
    const startMs = eventUtcMs(ev.date, ev.startTime || '00:00')
    if (startMs < bestStartMs) {
      bestStartMs = startMs
      best = ev
    }
  }
  return best
}

// Days-with-data + total-events count for one month — powers the
// calendar-dot month picker's giant footer stats (geex frame 29 "7 Days /
// 125 Orders", tickets/brief-product-ui-direction.md "Pickers"). Walks the
// SAME `getMonthDays` / `getEventsForDay` the grid itself renders from, so
// the footer can never disagree with what's actually on screen.
export function getMonthEventSummary(events, year, month) {
  const days = getMonthDays(year, month).filter((d) => !d.isOtherMonth)
  let daysWithData = 0
  let total = 0
  for (const d of days) {
    const n = getEventsForDay(events, year, month, d.day).length
    if (n > 0) daysWithData++
    total += n
  }
  return { days: daysWithData, events: total }
}

// Infer the recurrence pattern from sibling events sharing the same base
// google_event_id. Google Calendar recurring events encode the instance date
// as a suffix (e.g. `baseId_20260413T193000Z`), so all instances of the
// same series share a common prefix. We analyse the loaded siblings' dates
// to determine daily / weekly / biweekly / monthly patterns.
export function inferRecurrence(events, event) {
  // If the event already has explicit recurrence info, use it.
  if (event?.recurring && event.recurring !== 'none') {
    return { recurring: event.recurring, recurringDays: event.recurringDays || [] }
  }
  if (!event?.googleEventId) return { recurring: 'none', recurringDays: [] }

  const baseId = _baseEventId(event.googleEventId)
  // No date suffix → not a recurring instance
  if (!baseId || baseId === event.googleEventId) {
    return { recurring: 'none', recurringDays: [] }
  }

  const siblings = (events || []).filter((e) => _baseEventId(e.googleEventId) === baseId)
  if (siblings.length < 2) return { recurring: 'none', recurringDays: [] }

  // Unique sorted dates and their day-of-week
  const dateSet = new Set(siblings.map((e) => e.date))
  const dates = [...dateSet].sort()
  const parsed = dates.map((d) => {
    const [y, m, day] = d.split('-').map(Number)
    return new Date(y, m - 1, day)
  })
  const dows = [...new Set(parsed.map((d) => d.getDay()))]

  // Consecutive intervals in days
  const intervals = []
  for (let i = 1; i < parsed.length; i++) {
    intervals.push(Math.round((parsed[i] - parsed[i - 1]) / _DAY_MS))
  }
  if (!intervals.length) return { recurring: 'none', recurringDays: [] }

  // Pattern detection
  if (intervals.every((i) => i === 1)) return { recurring: 'daily', recurringDays: [] }
  if (intervals.every((i) => i === 7) && dows.length === 1)
    return { recurring: 'weekly', recurringDays: dows }
  if (intervals.every((i) => i === 14) && dows.length === 1)
    return { recurring: 'biweekly', recurringDays: [] }

  // Same day-of-month across months
  const doms = [...new Set(parsed.map((d) => d.getDate()))]
  if (doms.length === 1 && intervals.every((i) => i >= 28 && i <= 31)) {
    return { recurring: 'monthly', recurringDays: [] }
  }

  // Weekday-only pattern (Mon–Fri)
  const allWeekdays = dows.every((d) => d >= 1 && d <= 5)
  if (allWeekdays && dows.length === 5) return { recurring: 'weekdays', recurringDays: [] }

  // Multiple days per week — treat as weekly with specific days
  if (dows.length >= 1 && dows.length <= 5) {
    return { recurring: 'weekly', recurringDays: dows }
  }

  return { recurring: 'weekly', recurringDays: dows }
}

// Fresh newBooking payload — merges any caller-provided overrides on top of
// a clean baseline so previously-typed fields (description, meetUrl, etc.)
// don't leak between modal opens.
const _nextBookingDate = (date) => {
  if (!date) return ''
  const [year, month, day] = date.split('-').map(Number)
  const next = new Date(year, month - 1, day + 1)
  return formatDate(next.getFullYear(), next.getMonth(), next.getDate())
}

export function freshBooking(overrides = {}) {
  const booking = {
    title: '',
    date: '',
    endDate: '',
    startTime: '09:00',
    endTime: '10:00',
    allDay: false,
    invitees: '',
    inviteeQuery: '',
    description: '',
    type: 'meeting',
    meetingMode: 'symbols-meet',
    meetUrl: '',
    recurring: 'none',
    recurringDays: [],
    ...DEFAULT_RECURRENCE_DRAFT,
    // Minutes-before-event reminder (#2200). `null` = no reminder.
    // Server-side persistence depends on a `reminder_minutes` column on
    // calendar_events — until that lands, the value round-trips through
    // the optimistic event but does not survive a refresh. See
    // tickets/server.md → "Calendar event reminder_minutes column".
    reminderMinutes: null,
    editingEventId: null,
    ...overrides
  }
  if (!booking.date) return booking
  if (booking.endTime === '24:00') booking.endTime = '00:00'
  if (!booking.endDate) {
    booking.endDate =
      booking.endTime <= booking.startTime ? _nextBookingDate(booking.date) : booking.date
  }
  return booking
}

export function eventToTop(startTime) {
  // Guard against a not-yet-hydrated event chip (startTime unset) — the
  // real grid always supplies HH:MM, but a bare/mid-optimistic-update chip
  // shouldn't throw inside a reactive CSS factory (was `undefined.split`).
  if (!startTime) return 0
  const [h, m] = startTime.split(':').map(Number)
  return ((h * 60 + m) / 60) * 56
}

export function eventToHeight(startTime, endTime) {
  if (!startTime || !endTime) return 24
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const startMins = sh * 60 + sm
  // Cross-midnight: clip to end-of-day (24:00). The next-day tail is rendered
  // separately by getEventsForDay's wrap-around branch.
  let endMins = eh * 60 + em
  if (endMins <= startMins) endMins = 24 * 60
  return Math.max(((endMins - startMins) / 60) * 56, 24)
}

// Convert pixel offset within the time grid into HH:MM, snapped to 15-min.
export function pxToTime(px) {
  const totalMinutes = Math.max(0, Math.min(24 * 60 - 15, Math.round(((px / 56) * 60) / 15) * 15))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Add `mins` minutes to an HH:MM string.
export function addMinutes(time, mins) {
  const [h, m] = time.split(':').map(Number)
  const total = Math.min(24 * 60, h * 60 + m + mins)
  const nh = Math.floor(total / 60)
  const nm = total % 60
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`
}

export function getEventTheme(color) {
  const map = {
    blue: 'eventBlue',
    green: 'eventGreen',
    red: 'eventRed',
    purple: 'eventPurple',
    orange: 'eventOrange',
    indigo: 'eventIndigo'
  }
  return map[color] || 'eventBlue'
}

export function prevMonth(year, month) {
  if (month === 0) return { year: year - 1, month: 11 }
  return { year, month: month - 1 }
}

export function nextMonth(year, month) {
  if (month === 11) return { year: year + 1, month: 0 }
  return { year, month: month + 1 }
}

// Lazy-load `description` and `invitees` for a single event when its
// detail modal opens. The initial calendar fetch skips these fields
// (they're 68% of the payload) so the grid loads fast. Mutates the row
// in `root.events` so any view referencing the same id sees the new fields.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
// Cache the FETCHED DETAILS (not just a "seen" flag) keyed by event id.
// The home AppWindow pre-mounts /calendar and re-runs the lean events
// fetch on every re-open, which overwrites `root.events` with rows that
// lack description/invitees. The previous `Set` only recorded that an id
// had been fetched once and bailed on re-entry — so re-opening an event
// from `/` (after that lean overwrite) left the modal stuck on the lean
// row forever. Caching the data lets us RE-APPLY it on re-open without a
// network round-trip. `_detailInflight` dedupes concurrent fetches.
const _detailCache = new Map()
const _detailInflight = new Set()

// Extract the base recurring event ID (strip the date suffix).
// e.g. "09aql32kefq530bfigjd8gvarc_20260413T193000Z" → "09aql32kefq530bfigjd8gvarc"
const _baseEventId = (geid) => (geid ? geid.replace(/_\d{8}T\d{6}Z$/, '') : null)

// Merge resolved details into root.events (so every view sees them) and
// into selectedEvent (so the open modal updates).
const _applyEventDetails = (root, eventId, description, invitees) => {
  const events = root.events || []
  const next = events.map((e) => (e.id === eventId ? { ...e, description, invitees } : e))
  const updates = { events: next }
  if (root.selectedEvent?.id === eventId) {
    updates.selectedEvent = { ...root.selectedEvent, description, invitees }
  }
  root.update(updates)
}

export async function fetchEventDetails(eventId, root) {
  if (!eventId || !root) return
  // An edited row already contains authoritative details. Prefer it over an
  // older lazy-fetch snapshot and refresh the cache before the reopened modal
  // derives participant/RSVP visibility from invitees.
  const local = (root.events || []).find((event) => event.id === eventId)
  if (local && local.description !== undefined && Array.isArray(local.invitees)) {
    const details = { description: local.description, invitees: local.invitees }
    _detailCache.set(eventId, details)
    _applyEventDetails(root, eventId, details.description, details.invitees)
    return
  }
  // Already resolved — re-apply the cached details. Crucial for re-opens
  // from the home AppWindow, where the lean events fetch has since wiped
  // description/invitees off the row in `root.events`.
  const cached = _detailCache.get(eventId)
  if (cached) {
    _applyEventDetails(root, eventId, cached.description, cached.invitees)
    return
  }
  if (_detailInflight.has(eventId)) return
  _detailInflight.add(eventId)
  try {
    // BY ID — GET /calendar/events/:id answers this event's row (404 for a
    // missing, deleted or unreadable one: the catch below). The `list` read
    // this used before could not select one row: the SDK sends only `filter`
    // (no `limit`, no `columns`) and the server reads only start_at, tags and
    // tenantId from it, so `{ id }` answered EVERY readable event in start
    // order and `result[0]` put the earliest event's description and
    // participants on this one. Only a row whose id IS this event's id may
    // fill its details.
    const row = await getSDK().execute('workspaceProject.calendar', 'get', { id: eventId })
    const data = row && String(row.id) === String(eventId) ? row : null
    if (!data) return
    let invitees = data.invitees || []
    let description = data.description

    // If this instance has no invitees, inherit from a sibling (same recurring series)
    if (!invitees.length) {
      const ev = (root.events || []).find((e) => e.id === eventId)
      const baseId = _baseEventId(ev?.googleEventId)
      if (baseId) {
        const sibling = (root.events || []).find(
          (e) => e.id !== eventId && e.invitees?.length && _baseEventId(e.googleEventId) === baseId
        )
        if (sibling) {
          invitees = sibling.invitees
          if (!description && sibling.description) description = sibling.description
        }
      }
    }

    _detailCache.set(eventId, { description, invitees })
    _applyEventDetails(root, eventId, description, invitees)
  } catch (e) {
    // Surface the failure instead of swallowing it — a silent catch here
    // made "modal stuck on Loading…" impossible to diagnose. Not cached,
    // so the next open retries.
    console.warn('[calendar] fetchEventDetails failed:', eventId, e?.message || e)
  } finally {
    _detailInflight.delete(eventId)
  }
}

// ---------------------------------------------------------------------------
// Events-fetch window policy — the ONE definition shared by the page's
// declarative fetch (pages/index.js `params:`) and the imperative widen
// below. Covers the anchor's WHOLE month (a month view must never silently
// miss the back half of the month it is showing) plus two weeks of lead-in
// and the following month of look-ahead.
export function calendarFetchWindow(anchor) {
  const a = anchor instanceof Date ? anchor : new Date(anchor || Date.now())
  const monthStart = new Date(a.getFullYear(), a.getMonth(), 1)
  const monthEnd = new Date(a.getFullYear(), a.getMonth() + 1, 0, 23, 59, 59, 999)
  return {
    gte: new Date(monthStart.getTime() - 14 * _DAY_MS).toISOString(),
    lte: new Date(monthEnd.getTime() + 45 * _DAY_MS).toISOString()
  }
}

// Lean projection for events-grid fetches — description (16 KB) and
// invitees (92 KB) stay out; both lazy-load via fetchEventDetails.
export const CAL_LEAN_SELECT =
  'id,title,date,start_time,end_time,end_date,start_at,end_at,color,type,reminder_minutes,integration_metadata,organizer_email,google_event_id,google_calendar_id'

// Map a Supabase calendar_events row → the camelCase shape the calendar UI
// expects. Lives here (not pages/index.js) so the imperative widen below can
// use it without a functions→pages import cycle; pages/index.js re-exports
// it for its declarative transform.
//
// `userTz` (optional) — explicit timezone string (e.g. 'Asia/Tbilisi');
// without it the browser's local zone applies. See formatEventTimes for
// render-time recompute.
import {
  localDate as _localDate,
  localTime as _localTime
} from '@symbo-ls/workspace-shared/functions/dateUtils.js'
export const rowToEvent = (r, userTz) => {
  // Prefer the canonical UTC timestamps when present (synced rows). Fall
  // back to the legacy text fields for any rows created before migration
  // 0005 — those are stored in UTC strings already.
  let date = r.date
  let startTime = r.start_time
  let endTime = r.end_time
  let endDate = r.end_date
  if (r.start_at) {
    const s = new Date(r.start_at)
    date = _localDate(s, userTz)
    startTime = _localTime(s, userTz)
    if (r.end_at) {
      const e = new Date(r.end_at)
      endTime = _localTime(e, userTz)
      const ed = _localDate(e, userTz)
      if (ed !== date) endDate = ed
    }
  }
  const integrationMetadata =
    r.integration_metadata && typeof r.integration_metadata === 'object'
      ? r.integration_metadata
      : {}
  return {
    id: r.id,
    title: r.title,
    date,
    startTime,
    endTime,
    endDate,
    startAt: r.start_at || null,
    endAt: r.end_at || null,
    color: r.color,
    type: r.type || 'meeting',
    description: r.description,
    invitees: r.invitees,
    reminderMinutes: r.reminder_minutes ?? null,
    ...recurrenceFieldsFromMetadata(integrationMetadata),
    integrationMetadata,
    creatorEmail: r.organizer_email,
    googleEventId: r.google_event_id,
    googleCalendarId: r.google_calendar_id
  }
}

// Scroll the active time-grid view so its live red Now indicator sits near
// the viewport centre. The actual vertical scrollport differs by layout:
// Week owns it in ScrollBody on desktop and at the view root on phones, while
// Team / Team Now scroll at their view roots. Walking up from the visible
// indicator keeps this independent of those responsive ownership changes.
export function scrollCalendarToNow() {
  const stateRoot = this?.state?.root || this?.getRoot?.()?.state?.root
  const viewKey = {
    week: 'CalWeekView',
    day: 'CalWeekView',
    team: 'CalTeamView',
    'team-now': 'CalTeamNowView'
  }[stateRoot?.viewMode]
  if (!viewKey) return false

  const rootNode = this?.getRoot?.()?.node || this?.node?.ownerDocument?.body
  const view = rootNode?.querySelector?.(`[data-key="${viewKey}"]`)
  if (!view) return false

  const indicators = Array.from(
    view.querySelectorAll?.('[data-key="NowIndicator"], [data-key="NowLineWrap"]') || []
  )
  const indicator =
    indicators.find((node) => (node.getClientRects?.().length || 0) > 0) || indicators[0]
  if (!indicator) return false

  const scroll = () => {
    let scroller = indicator.parentElement
    while (scroller && scroller !== view.parentElement) {
      if (scroller.clientHeight > 0 && scroller.scrollHeight > scroller.clientHeight) break
      scroller = scroller.parentElement
    }
    if (!scroller || scroller === view.parentElement) return

    const indicatorRect = indicator.getBoundingClientRect()
    const scrollerRect = scroller.getBoundingClientRect()
    const indicatorY = indicatorRect.top - scrollerRect.top + scroller.scrollTop
    const top = Math.max(0, indicatorY - scroller.clientHeight / 2)
    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ top, behavior: 'smooth' })
    } else {
      scroller.scrollTop = top
    }
  }

  // The Today state update can replace the visible week before the indicator
  // moves into today's column. Two frames let DOMQL finish that reactive pass
  // and the browser finish layout before measurements are taken.
  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : null
  if (raf) raf(() => raf(scroll))
  else scroll()
  return true
}

// Imperative window widen — called from every calendar-cursor nav site
// (month arrows, week/team arrows, Today, mini-calendar). The declarative
// page fetch loads calendarFetchWindow(today) once at mount and NEVER
// re-runs (the @symbo.ls/fetch plugin has no reactive re-fetch), so before
// this existed any event beyond the initial window was invisible in every
// view — the "wider window fetched on demand" the page comment promised
// did not exist (caught live 2026-08-06 via bellforge module events 15+
// days out).
//
// Keeps `root._calWindow` as the loaded-range bookkeeping: no-op when the
// target month is already covered, otherwise fetches the target month's
// full window and MERGES into root.events (dedupe by google_event_id |
// local-id, fresh row wins — same key the modal detail cache uses).
export async function ensureCalendarWindow(root, year, month) {
  const el = this
  if (!root || year == null || month == null) return { ok: false }
  const fetcher = el && el.context && el.context.fetch
  if (!fetcher || typeof fetcher.select !== 'function') return { ok: false }

  const monthStartISO = new Date(year, month, 1).toISOString()
  const monthEndISO = new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString()
  const w = root._calWindow
  if (w && w.gte <= monthStartISO && w.lte >= monthEndISO) {
    return { ok: true, widened: false }
  }
  if (root._calWindowLoading) return { ok: true, widened: false, pending: true }

  const target = calendarFetchWindow(new Date(year, month, 15))
  const union = {
    gte: w && w.gte < target.gte ? w.gte : target.gte,
    lte: w && w.lte > target.lte ? w.lte : target.lte
  }
  root.update({ _calWindowLoading: true })
  try {
    const result = await fetcher.select({
      from: 'workspaceProject.calendar',
      select: CAL_LEAN_SELECT,
      params: { deleted: false, start_at: { gte: target.gte, lte: target.lte } },
      order: 'start_at',
      limit: 2000
    })
    const rows = result && result.data
    if (!rows) {
      root.update({ _calWindowLoading: false })
      return { ok: false, widened: false }
    }
    const merged = new Map()
    for (const r of rows) {
      const ev = rowToEvent(r)
      merged.set(ev.googleEventId || `local-${ev.id}`, ev)
    }
    for (const ev of root.events || []) {
      const k = ev.googleEventId || `local-${ev.id}`
      if (!merged.has(k)) merged.set(k, ev)
    }
    const events = [...merged.values()].sort((a, b) =>
      String(a.startAt || a.date).localeCompare(String(b.startAt || b.date))
    )
    root.update({ events, _calWindow: union, _calWindowLoading: false })
    return { ok: true, widened: true }
  } catch (e) {
    console.warn('[calendar] ensureCalendarWindow failed:', e?.message || e)
    root.update({ _calWindowLoading: false })
    return { ok: false, error: String(e?.message || e) }
  }
}
